import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DynamicFilters from '../components/DynamicFilters';
import { API_BASE_URL, productsPublicApi } from '../services/api';

interface CategoryField {
  id: string;
  fieldName: string;
  label: string;
  fieldType: string;
  filterType?: string;
  isFilterable: boolean;
  isRequired: boolean;
  displayOrder: number;
  options?: { value: string; label: string; count?: number }[];
  min?: number;
  max?: number;
  unit?: string;
}

interface Category {
  id: string;
  slug: string;
  name: string;
  icon: string;
  fields: {
    required: CategoryField[];
    recommended: CategoryField[];
    optional: CategoryField[];
  } | CategoryField[]; // Support both formats
}

// Fallback categories (used when API is not available)
const FALLBACK_CATEGORIES: Category[] = [
  {
    id: 'all',
    name: 'All Products',
    icon: '🔍',
    slug: 'all',
    fields: [],
  },
  {
    id: 'home-garden-outdoor-bbq',
    slug: 'bbq',
    name: 'BBQs',
    icon: '🔥',
    fields: [
      { id: 'fuel_type', fieldName: 'fuel_type', label: 'Fuel Type', filterType: 'multi-select' as const, displayOrder: 1, isFilterable: true, isRequired: true, fieldType: 'select' as const,
        options: [
          { value: 'gas', label: 'Gas', count: 189 },
          { value: 'charcoal', label: 'Charcoal', count: 78 },
          { value: 'electric', label: 'Electric', count: 45 },
          { value: 'pellet', label: 'Pellet', count: 23 },
          { value: 'hybrid', label: 'Hybrid', count: 12 },
        ]
      },
      { id: 'burners', fieldName: 'burners', label: 'Burners', filterType: 'discrete' as const, displayOrder: 2, isFilterable: true, isRequired: true, fieldType: 'number' as const,
        options: [
          { value: '2', label: '2 Burners', count: 45 },
          { value: '3', label: '3 Burners', count: 67 },
          { value: '4', label: '4 Burners', count: 89 },
          { value: '5', label: '5 Burners', count: 56 },
          { value: '6', label: '6+ Burners', count: 34 },
        ]
      },
      { id: 'has_rotisserie', fieldName: 'has_rotisserie', label: 'Rotisserie', filterType: 'checkbox' as const, displayOrder: 3, isFilterable: true, isRequired: false, fieldType: 'boolean' as const },
      { id: 'has_side_burner', fieldName: 'has_side_burner', label: 'Side Burner', filterType: 'checkbox' as const, displayOrder: 4, isFilterable: true, isRequired: false, fieldType: 'boolean' as const },
      { id: 'brand', fieldName: 'brand', label: 'Brand', filterType: 'multi-select' as const, displayOrder: 5, isFilterable: true, isRequired: false, fieldType: 'text' as const,
        options: [
          { value: 'weber', label: 'Weber', count: 45 },
          { value: 'beefeater', label: 'BeefEater', count: 38 },
          { value: 'ziegler', label: 'Ziegler & Brown', count: 29 },
          { value: 'masport', label: 'Masport', count: 22 },
        ]
      },
    ],
  },
  {
    id: 'electronics-computers',
    slug: 'computers',
    name: 'Computers',
    icon: '💻',
    fields: [
      { id: 'type', fieldName: 'type', label: 'Type', filterType: 'multi-select' as const, displayOrder: 1, isFilterable: true, isRequired: true, fieldType: 'select' as const,
        options: [
          { value: 'laptop', label: 'Laptop', count: 234 },
          { value: 'desktop', label: 'Desktop', count: 156 },
          { value: 'all-in-one', label: 'All-in-One', count: 45 },
          { value: 'mini-pc', label: 'Mini PC', count: 23 },
        ]
      },
      { id: 'ram_gb', fieldName: 'ram_gb', label: 'RAM', filterType: 'discrete' as const, displayOrder: 2, isFilterable: true, isRequired: false, fieldType: 'select' as const,
        options: [
          { value: '8', label: '8GB', count: 89 },
          { value: '16', label: '16GB', count: 156 },
          { value: '32', label: '32GB', count: 78 },
          { value: '64', label: '64GB', count: 34 },
        ]
      },
      { id: 'storage_gb', fieldName: 'storage_gb', label: 'Storage', filterType: 'discrete' as const, displayOrder: 3, isFilterable: true, isRequired: false, fieldType: 'select' as const,
        options: [
          { value: '256', label: '256GB', count: 67 },
          { value: '512', label: '512GB', count: 123 },
          { value: '1000', label: '1TB', count: 145 },
          { value: '2000', label: '2TB+', count: 56 },
        ]
      },
      { id: 'brand', fieldName: 'brand', label: 'Brand', filterType: 'multi-select' as const, displayOrder: 4, isFilterable: true, isRequired: false, fieldType: 'text' as const,
        options: [
          { value: 'apple', label: 'Apple', count: 89 },
          { value: 'dell', label: 'Dell', count: 67 },
          { value: 'hp', label: 'HP', count: 56 },
          { value: 'lenovo', label: 'Lenovo', count: 78 },
          { value: 'asus', label: 'ASUS', count: 45 },
        ]
      },
    ],
  },
  {
    id: 'furniture-beds',
    slug: 'beds',
    name: 'Beds & Mattresses',
    icon: '🛏️',
    fields: [
      { id: 'size', fieldName: 'size', label: 'Size', filterType: 'multi-select' as const, displayOrder: 1, isFilterable: true, isRequired: true, fieldType: 'select' as const,
        options: [
          { value: 'single', label: 'Single', count: 34 },
          { value: 'king_single', label: 'King Single', count: 28 },
          { value: 'double', label: 'Double', count: 45 },
          { value: 'queen', label: 'Queen', count: 89 },
          { value: 'king', label: 'King', count: 67 },
          { value: 'super_king', label: 'Super King', count: 23 },
        ]
      },
      { id: 'mattress_type', fieldName: 'mattress_type', label: 'Mattress Type', filterType: 'multi-select' as const, displayOrder: 2, isFilterable: true, isRequired: false, fieldType: 'select' as const,
        options: [
          { value: 'innerspring', label: 'Innerspring', count: 45 },
          { value: 'memory_foam', label: 'Memory Foam', count: 78 },
          { value: 'latex', label: 'Latex', count: 34 },
          { value: 'hybrid', label: 'Hybrid', count: 56 },
          { value: 'pocket_spring', label: 'Pocket Spring', count: 67 },
        ]
      },
      { id: 'firmness', fieldName: 'firmness', label: 'Firmness', filterType: 'multi-select' as const, displayOrder: 3, isFilterable: true, isRequired: false, fieldType: 'select' as const,
        options: [
          { value: 'soft', label: 'Soft', count: 23 },
          { value: 'medium', label: 'Medium', count: 89 },
          { value: 'firm', label: 'Firm', count: 67 },
          { value: 'extra_firm', label: 'Extra Firm', count: 34 },
        ]
      },
      { id: 'has_storage', fieldName: 'has_storage', label: 'With Storage', filterType: 'checkbox' as const, displayOrder: 4, isFilterable: true, isRequired: false, fieldType: 'boolean' as const },
    ],
  },
  {
    id: 'services-plumber',
    slug: 'plumber',
    name: 'Plumbers',
    icon: '🔧',
    fields: [
      { id: 'service_type', fieldName: 'service_type', label: 'Service Type', filterType: 'multi-select' as const, displayOrder: 1, isFilterable: true, isRequired: true, fieldType: 'multi-select' as const,
        options: [
          { value: 'emergency', label: 'Emergency', count: 45 },
          { value: 'installation', label: 'Installation', count: 67 },
          { value: 'repair', label: 'Repair', count: 89 },
          { value: 'maintenance', label: 'Maintenance', count: 34 },
        ]
      },
      { id: 'availability', fieldName: 'availability', label: 'Availability', filterType: 'multi-select' as const, displayOrder: 2, isFilterable: true, isRequired: false, fieldType: 'select' as const,
        options: [
          { value: '24_7', label: '24/7', count: 23 },
          { value: 'business_hours', label: 'Business Hours', count: 56 },
          { value: 'weekends', label: 'Weekends', count: 34 },
          { value: 'after_hours', label: 'After Hours', count: 28 },
        ]
      },
      { id: 'licensed', fieldName: 'licensed', label: 'Licensed', filterType: 'checkbox' as const, displayOrder: 3, isFilterable: true, isRequired: true, fieldType: 'boolean' as const },
      { id: 'insured', fieldName: 'insured', label: 'Insured', filterType: 'checkbox' as const, displayOrder: 4, isFilterable: true, isRequired: true, fieldType: 'boolean' as const },
      { id: 'hourly_rate', fieldName: 'hourly_rate', label: 'Hourly Rate', filterType: 'range' as const, displayOrder: 5, isFilterable: true, isRequired: false, fieldType: 'number' as const, unit: '$/hr' },
    ],
  },
  {
    id: 'office-pens',
    slug: 'pens',
    name: 'Pens',
    icon: '🖊️',
    fields: [
      { id: 'pen_type', fieldName: 'pen_type', label: 'Type', filterType: 'multi-select' as const, displayOrder: 1, isFilterable: true, isRequired: true, fieldType: 'select' as const,
        options: [
          { value: 'ballpoint', label: 'Ballpoint', count: 156 },
          { value: 'rollerball', label: 'Rollerball', count: 89 },
          { value: 'gel', label: 'Gel', count: 123 },
          { value: 'fountain', label: 'Fountain', count: 34 },
          { value: 'felt_tip', label: 'Felt Tip', count: 45 },
        ]
      },
      { id: 'ink_color', fieldName: 'ink_color', label: 'Ink Color', filterType: 'multi-select' as const, displayOrder: 2, isFilterable: true, isRequired: false, fieldType: 'select' as const,
        options: [
          { value: 'black', label: 'Black', count: 234 },
          { value: 'blue', label: 'Blue', count: 189 },
          { value: 'red', label: 'Red', count: 67 },
          { value: 'green', label: 'Green', count: 45 },
          { value: 'multi', label: 'Multi-color', count: 23 },
        ]
      },
      { id: 'tip_size', fieldName: 'tip_size', label: 'Tip Size', filterType: 'multi-select' as const, displayOrder: 3, isFilterable: true, isRequired: false, fieldType: 'select' as const,
        options: [
          { value: 'fine', label: 'Fine (0.5mm)', count: 89 },
          { value: 'medium', label: 'Medium (0.7mm)', count: 145 },
          { value: 'bold', label: 'Bold (1.0mm)', count: 67 },
        ]
      },
      { id: 'retractable', fieldName: 'retractable', label: 'Retractable', filterType: 'checkbox' as const, displayOrder: 4, isFilterable: true, isRequired: false, fieldType: 'boolean' as const },
      { id: 'has_grip', fieldName: 'has_grip', label: 'Rubber Grip', filterType: 'checkbox' as const, displayOrder: 5, isFilterable: true, isRequired: false, fieldType: 'boolean' as const },
    ],
  },
];

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  imageUrl: string;
  businessName: string;
  businessId: string;
  category: string;
  categoryId: string;
  attributes: Record<string, any>;
  rating: number;
  reviewCount: number;
  location?: string;
  distance?: string;
}

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  useAuth();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [categories, setCategories] = useState<Category[]>(FALLBACK_CATEGORIES);
  const [, setCategoriesLoading] = useState(true);

  // Fetch categories from API (same source as Dashboard)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories/fields`);
        if (response.ok) {
          const data = await response.json();
          // Add "All Products" category at the start
          const apiCategories = data.data || data;
          const allCategory = { id: 'all', slug: 'all', name: 'All Products', icon: '🔍', fields: { required: [], recommended: [], optional: [] }, allowCustomFields: false };
          setCategories([allCategory, ...apiCategories]);
        }
      } catch (error) {
        console.error('Failed to fetch categories, using fallback:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const currentCategory = categories.find(c => c.id === selectedCategory) || categories[0];

  // Helper to get filterable fields from category (handles both old and new structure)
  const getCategoryFilters = (cat: Category): CategoryField[] => {
    if (!cat) return [];
    
    // If fields is an array (old format), use directly
    if (Array.isArray(cat.fields)) {
      return cat.fields.filter((f: any) => f.isFilterable);
    }
    
    // If fields is nested object (new API format), flatten it
    if (cat.fields && typeof cat.fields === 'object') {
      const allFields = [
        ...(cat.fields.required || []),
        ...(cat.fields.recommended || []),
        ...(cat.fields.optional || []),
      ];
      return allFields.filter((f: any) => f.isFilterable).sort((a, b) => a.displayOrder - b.displayOrder);
    }
    
    return [];
  };

  const currentFilters = getCategoryFilters(currentCategory);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadProducts uses deps from closure
  }, [selectedCategory, activeFilters, sortBy, searchQuery]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await productsPublicApi.list({
        search: searchQuery || undefined,
        category: selectedCategory === 'all' ? undefined : selectedCategory,
        page: 1,
        limit: 50,
      });
      const rawList = res?.products ?? res?.data ?? [];
      const mapped: Product[] = (Array.isArray(rawList) ? rawList : []).map((p: any) => {
        const variant = p.variants?.[0];
        const price = variant?.price ?? p.price ?? 0;
        return {
          id: p.id,
          name: p.name ?? '',
          description: p.description ?? p.shortDescription ?? '',
          price: typeof price === 'number' ? price : parseFloat(String(price)) || 0,
          currency: variant?.currency ?? p.currency ?? 'AUD',
          imageUrl: p.primaryImage ?? p.imageUrl ?? '',
          businessName: p.business?.name ?? '',
          businessId: p.business?.id ?? p.businessId ?? '',
          category: p.category ?? '',
          categoryId: p.category ?? p.categoryId ?? '',
          attributes: p.attributes ?? p.customFields ?? {},
          rating: p.rating ?? 0,
          reviewCount: p.reviewCount ?? 0,
          location: p.location ?? p.business?.location,
          distance: p.distance,
        };
      });

      let filtered = mapped;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter(p =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.businessName.toLowerCase().includes(q)
        );
      }
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return;
        if (key === 'price' && typeof value === 'object') {
          if (value.min) filtered = filtered.filter(p => p.price >= value.min);
          if (value.max) filtered = filtered.filter(p => p.price <= value.max);
        } else if (Array.isArray(value)) {
          filtered = filtered.filter(p => {
            const attrValue = p.attributes[key];
            if (Array.isArray(attrValue)) return value.some((v: any) => attrValue.includes(v));
            return value.includes(String(attrValue));
          });
        } else if (typeof value === 'boolean') {
          filtered = filtered.filter(p => p.attributes[key] === value);
        }
      });
      if (sortBy === 'price_low') filtered = [...filtered].sort((a, b) => a.price - b.price);
      else if (sortBy === 'price_high') filtered = [...filtered].sort((a, b) => b.price - a.price);
      else if (sortBy === 'rating') filtered = [...filtered].sort((a, b) => b.rating - a.rating);
      setProducts(filtered);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterId: string, value: any) => {
    setActiveFilters({ ...activeFilters, [filterId]: value });
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setActiveFilters({});
    setSearchParams({ category: categoryId });
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Search Header */}
      <div style={{
        background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%)',
        padding: '30px',
        borderRadius: '16px',
        marginBottom: '25px',
        color: 'white'
      }}>
        <h1 style={{ margin: '0 0 15px 0', fontSize: '24px', fontWeight: '700' }}>
          🔍 Search All Mango Products
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="What are you looking for?"
            style={{
              flex: 1,
              padding: '15px 20px',
              fontSize: '16px',
              border: 'none',
              borderRadius: '12px',
              outline: 'none'
            }}
          />
          <button style={{
            padding: '15px 30px',
            background: '#1a1a2e',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '16px'
          }}>
            Search
          </button>
        </div>
      </div>

      {/* Category Selector */}
      <div style={{
        display: 'flex',
        gap: '10px',
        overflowX: 'auto',
        padding: '10px 0',
        marginBottom: '20px'
      }}>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => handleCategoryChange(cat.id)}
            style={{
              padding: '12px 20px',
              background: selectedCategory === cat.id ? '#ff6b35' : 'white',
              color: selectedCategory === cat.id ? 'white' : '#333',
              border: selectedCategory === cat.id ? 'none' : '1px solid #ddd',
              borderRadius: '25px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: selectedCategory === cat.id ? '600' : '400',
              fontSize: '14px',
              boxShadow: selectedCategory === cat.id ? '0 4px 12px rgba(255, 107, 53, 0.3)' : 'none'
            }}
          >
            <span style={{ fontSize: '18px' }}>{cat.icon}</span>
            {cat.name}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', gap: '25px' }}>
        {/* Filters Sidebar - Desktop */}
        <div style={{ 
          width: '280px', 
          flexShrink: 0,
          display: window.innerWidth < 768 ? 'none' : 'block'
        }}>
          {currentFilters.length > 0 && (
            <DynamicFilters
              categoryId={selectedCategory}
              filters={currentFilters as any}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              onClearAll={handleClearFilters}
            />
          )}

          {/* Location Filter */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <h4 style={{ margin: '0 0 15px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
              📍 Location
            </h4>
            <input
              type="text"
              placeholder="Suburb or postcode"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '13px',
                marginBottom: '10px'
              }}
            />
            <select style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '13px',
              background: 'white'
            }}>
              <option value="10">Within 10 km</option>
              <option value="25">Within 25 km</option>
              <option value="50" selected>Within 50 km</option>
              <option value="100">Within 100 km</option>
              <option value="">Any distance</option>
            </select>
          </div>
        </div>

        {/* Results */}
        <div style={{ flex: 1 }}>
          {/* Results Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                Found <strong style={{ color: '#ff6b35' }}>{products.length}</strong> results
                {searchQuery && <> for "<strong>{searchQuery}</strong>"</>}
                {selectedCategory !== 'all' && <> in <strong>{currentCategory.name}</strong></>}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '10px 15px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '13px',
                  background: 'white'
                }}
              >
                <option value="relevance">Most Relevant</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="distance">Nearest</option>
              </select>
              <div style={{ display: 'flex', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                <button
                  onClick={() => setViewMode('grid')}
                  style={{
                    padding: '10px 15px',
                    background: viewMode === 'grid' ? '#ff6b35' : 'white',
                    color: viewMode === 'grid' ? 'white' : '#666',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  ⊞
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  style={{
                    padding: '10px 15px',
                    background: viewMode === 'list' ? '#ff6b35' : 'white',
                    color: viewMode === 'list' ? 'white' : '#666',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  ☰
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Filters Button */}
          <button
            onClick={() => setShowMobileFilters(true)}
            style={{
              display: window.innerWidth < 768 ? 'block' : 'none',
              width: '100%',
              padding: '12px',
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              marginBottom: '15px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🎚️ Filters {Object.keys(activeFilters).length > 0 && `(${Object.keys(activeFilters).length})`}
          </button>

          {/* Products Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>⏳</div>
              <p>Searching...</p>
            </div>
          ) : products.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px',
              background: 'white',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '15px' }}>🔍</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>No products found</h3>
              <p style={{ color: '#666' }}>Try adjusting your filters or search terms</p>
              <button
                onClick={handleClearFilters}
                style={{
                  marginTop: '15px',
                  padding: '12px 25px',
                  background: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : '1fr',
              gap: '20px'
            }}>
              {products.map(product => (
                <div
                  key={product.id}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    display: viewMode === 'list' ? 'flex' : 'block',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  }}
                >
                  <div style={{
                    width: viewMode === 'list' ? '200px' : '100%',
                    height: viewMode === 'list' ? '150px' : '200px',
                    background: `url(${product.imageUrl}) center/cover`,
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      top: '10px',
                      left: '10px',
                      background: 'rgba(255,255,255,0.95)',
                      padding: '4px 10px',
                      borderRadius: '15px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#ff6b35'
                    }}>
                      {product.category}
                    </span>
                    {product.distance && (
                      <span style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '15px',
                        fontSize: '11px'
                      }}>
                        📍 {product.distance}
                      </span>
                    )}
                  </div>
                  <div style={{ padding: '20px', flex: 1 }}>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1a1a2e',
                      lineHeight: 1.3
                    }}>
                      {product.name}
                    </h3>
                    <p style={{
                      margin: '0 0 12px 0',
                      fontSize: '13px',
                      color: '#666',
                      lineHeight: 1.4
                    }}>
                      {product.description}
                    </p>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '10px'
                    }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#ff6b35' }}>
                        ${product.price.toLocaleString()}
                        <span style={{ fontSize: '12px', fontWeight: '400', color: '#999' }}> {product.currency}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ color: '#ffc107' }}>★</span>
                        <span style={{ fontSize: '14px', fontWeight: '600' }}>{product.rating}</span>
                        <span style={{ fontSize: '12px', color: '#999' }}>({product.reviewCount})</span>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      🏪 {product.businessName}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <div style={{
            width: '85%',
            maxWidth: '350px',
            background: '#f5f5f5',
            height: '100%',
            overflowY: 'auto',
            padding: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0 }}>Filters</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                style={{
                  padding: '10px 20px',
                  background: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Apply
              </button>
            </div>
            <DynamicFilters
              categoryId={selectedCategory}
              filters={currentFilters as any}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              onClearAll={handleClearFilters}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;
